import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Set up the localizer for react-big-calendar
const localizer = momentLocalizer(moment);
const API_BASE_URL =
  process.env.REACT_APP_NODE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || "https://legailyjs.onrender.com";

const AdvDiary = () => {
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    matterNo: "",
    partyName: "",
    upcomingDate: "",
    upcomingTime: "",
    upcomingAmPm: "AM",
    nextDate: "",
    stage: "",
    notes: "",
    document: null,
  });
  const [todayMatters, setTodayMatters] = useState([]);
  const [expandedMatter, setExpandedMatter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedEntries, setExpandedEntries] = useState([]);
  const [mailStatus, setMailStatus] = useState("");

  const checkTodayMatters = useCallback(() => {
    const today = moment().startOf("day");
    const todaysMatters = entries.filter((entry) =>
      moment(entry.upcomingDate).isSame(today, "day")
    );
    setTodayMatters(todaysMatters);
  }, [entries]);

  useEffect(() => {
    checkTodayMatters();
  }, [checkTodayMatters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry({ ...newEntry, [name]: value });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewEntry({
          ...newEntry,
          document: {
            name: file.name,
            content: event.target.result,
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const addEntry = () => {
    const formattedTime = moment(
      `${newEntry.upcomingTime} ${newEntry.upcomingAmPm}`,
      "hh:mm A"
    ).format("HH:mm");
    const newEntryWithId = {
      ...newEntry,
      id: Date.now(),
      upcomingTime: formattedTime,
    };
    setEntries([...entries, newEntryWithId]);
    sendEntryEmail(newEntryWithId);
    setNewEntry({
      matterNo: "",
      partyName: "",
      upcomingDate: "",
      upcomingTime: "",
      upcomingAmPm: "AM",
      nextDate: "",
      stage: "",
      notes: "",
      document: null,
    });
  };

  const sendEntryEmail = async (entry) => {
    const recipientEmail = localStorage.getItem("email");
    if (!recipientEmail) {
      setMailStatus("Could not send email: recipient email not found.");
      return;
    }

    const payload = {
      to_email: recipientEmail,
      subject: `Adv Diary Entry: ${entry.matterNo} - ${entry.partyName}`,
      entry: {
        matterNo: entry.matterNo,
        partyName: entry.partyName,
        upcomingDate: entry.upcomingDate,
        upcomingTime: moment(entry.upcomingTime, "HH:mm").format("hh:mm A"),
        nextDate: entry.nextDate,
        stage: entry.stage,
        notes: entry.notes,
      },
      attachment: entry.document
        ? {
            name: entry.document.name,
            content: entry.document.content,
          }
        : null,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/adv-diary/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Email request failed with ${response.status}`);
      }

      setMailStatus("Entry added and email sent successfully.");
    } catch (error) {
      console.error("Failed to send adv diary email:", error);
      setMailStatus("Entry added, but email could not be sent.");
    }
  };

  const toggleMatterDetails = (matterId) => {
    setExpandedMatter(expandedMatter === matterId ? null : matterId);
  };

  const createBlobFromDocument = (document) => {
    if (document && document.content) {
      const byteCharacters = atob(document.content.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: "application/pdf" });
    }
    return null;
  };

  const viewDocument = (doc) => {
    const blob = createBlobFromDocument(doc);
    if (blob) {
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");
    } else {
      alert("No document available for viewing.");
    }
  };

  const downloadDocument = (doc) => {
    const blob = createBlobFromDocument(doc);
    if (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = fileURL;
      link.download = doc.name || "document.pdf";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } else {
      alert("No document available for download.");
    }
  };

  const calendarEvents = entries.map((entry) => ({
    title: `${entry.matterNo} - ${entry.partyName}`,
    start: moment(`${entry.upcomingDate} ${entry.upcomingTime}`).toDate(),
    end: moment(`${entry.upcomingDate} ${entry.upcomingTime}`)
      .add(1, "hour")
      .toDate(),
  }));

  const eventStyleGetter = (event, start, end, isSelected) => {
    const style = {
      backgroundColor: "#FF8C00",
      borderRadius: "5px",
      opacity: 0.8,
      color: "white",
      border: "0px",
      display: "block",
    };
    return {
      style: style,
    };
  };

  const toggleEntryDetails = (entryId) => {
    setExpandedEntries((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId]
    );
  };

  const filteredEntries = entries.filter((entry) =>
    entry.matterNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Advocate Diary</h1>
        <p className="text-gray-600 mt-1">Manage hearings, notes, and schedule in one consistent dashboard layout.</p>
      </div>
      <div className="container mx-auto">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-8 pt-6 pb-8">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Add New Entry
            </h2>
            <div className="mb-4">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-500 leading-tight focus:outline-none focus:shadow-outline"
                type="text"
                name="matterNo"
                placeholder="Matter Number"
                value={newEntry.matterNo}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-4">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                type="text"
                name="partyName"
                placeholder="Party Name"
                value={newEntry.partyName}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-4">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                type="date"
                name="upcomingDate"
                placeholder="Upcoming Date"
                value={newEntry.upcomingDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-4 flex">
              <input
                className="shadow appearance-none border rounded w-2/3 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-2"
                type="time"
                name="upcomingTime"
                placeholder="Hearing Time"
                value={newEntry.upcomingTime}
                onChange={handleInputChange}
              />
              <select
                className="shadow appearance-none border rounded w-1/3 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                name="upcomingAmPm"
                value={newEntry.upcomingAmPm}
                onChange={handleInputChange}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
            <div className="mb-4">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                type="date"
                name="nextDate"
                placeholder="Next Date"
                value={newEntry.nextDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-4">
              <select
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                name="stage"
                value={newEntry.stage}
                onChange={handleInputChange}
              >
                <option value="">Select Stage</option>
                <option value="Hearing">Hearing</option>
                <option value="Affidavit">Affidavit</option>
                <option value="Arguments">Arguments</option>
                <option value="Judgment">Judgment</option>
              </select>
            </div>
            <div className="mb-4">
              <textarea
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                name="notes"
                placeholder="Additional Notes"
                value={newEntry.notes}
                onChange={handleInputChange}
              ></textarea>
            </div>
            <div className="mb-4">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                type="file"
                accept="application/pdf"
                onChange={handleFileUpload}
              />
            </div>
            {newEntry.document && (
              <p className="mb-4">{newEntry.document.name}</p>
            )}
            <button
              className="bg-gradient-to-r from-gray-700 to-gray-500 hover:from-gray-800 hover:to-gray-600 text-white font-bold py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
              onClick={addEntry}
            >
              Add Entry
            </button>
          </div>
          <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-8 pt-6 pb-8">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Calendar
            </h2>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 500 }}
              eventPropGetter={eventStyleGetter}
              views={["month", "week", "day"]}
              tooltipAccessor={(event) =>
                `${event.title}\n${moment(event.start).format("hh:mm A")}`
              }
            />
          </div>
        </div>
        {mailStatus && (
          <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
            {mailStatus}
          </div>
        )}

        <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-8 pt-6 pb-8 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            Today's Matters
          </h2>
          {todayMatters.length === 0 ? (
            <p className="text-orange-800">No matters scheduled for today.</p>
          ) : (
            todayMatters.map((matter) => (
              <div key={matter.id} className="border-b border-orange-200 py-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-orange-800">
                    {matter.matterNo} - {matter.partyName}
                  </h3>
                  <button
                    className="text-orange-600 hover:text-orange-800"
                    onClick={() => toggleMatterDetails(matter.id)}
                  >
                    {expandedMatter === matter.id
                      ? "Hide Details"
                      : "Show Details"}
                  </button>
                </div>
                {expandedMatter === matter.id && (
                  <div className="mt-4 text-orange-800">
                    <p>
                      <strong>Upcoming Date:</strong> {matter.upcomingDate} at{" "}
                      {moment(matter.upcomingTime, "HH:mm").format("hh:mm A")}
                    </p>
                    <p>
                      <strong>Next Date:</strong> {matter.nextDate}
                    </p>
                    <p>
                      <strong>Stage:</strong> {matter.stage}
                    </p>
                    <p>
                      <strong>Notes:</strong> {matter.notes}
                    </p>
                    {matter.document && (
                      <div className="mt-2">
                        <button
                          className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-bold py-1 px-2 rounded mr-2"
                          onClick={() => viewDocument(matter.document)}
                        >
                          View Document
                        </button>
                        <button
                          className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-bold py-1 px-2 rounded"
                          onClick={() => downloadDocument(matter.document)}
                        >
                          Download Document
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-8 pt-6 pb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            All Diary Entries
          </h2>
          <div className="mb-4">
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text"
              placeholder="Search by Matter Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {filteredEntries.map((entry) => (
            <div
              key={entry.id}
              className="border-b border-orange-200 py-4 text-orange-800"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  {entry.matterNo} - {entry.partyName}
                </h3>
                <button
                  className="text-orange-600 hover:text-orange-800"
                  onClick={() => toggleEntryDetails(entry.id)}
                >
                  {expandedEntries.includes(entry.id)
                    ? "Hide Details"
                    : "Show Details"}
                </button>
              </div>
              {expandedEntries.includes(entry.id) && (
                <div className="mt-4">
                  <p>
                    <strong>Upcoming Date:</strong> {entry.upcomingDate} at{" "}
                    {moment(entry.upcomingTime, "HH:mm").format("hh:mm A")}
                  </p>
                  <p>
                    <strong>Next Date:</strong> {entry.nextDate}
                  </p>
                  <p>
                    <strong>Stage:</strong> {entry.stage}
                  </p>
                  <p>
                    <strong>Notes:</strong> {entry.notes}
                  </p>
                  {entry.document && (
                    <div className="mt-2">
                      <button
                        className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-bold py-1 px-2 rounded mr-2"
                        onClick={() => viewDocument(entry.document)}
                      >
                        View Document
                      </button>
                      <button
                        className="bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-bold py-1 px-2 rounded"
                        onClick={() => downloadDocument(entry.document)}
                      >
                        Download Document
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvDiary;
